import type { KitchenComboFormatted, KitchenProductFormatted } from '../../kitchenOrderFormatter'
import { BODY_FONT, splitLines, wrapText } from '../text.utils'
import type { TicketElement } from '../TicketElement'

export class KitchenProduct implements TicketElement {
  product: KitchenProductFormatted | KitchenComboFormatted
  pickupType: string | undefined
  quantityWidth: number
  priceWidth: number
  lineHeight: number
  paddingBottom: number
  fontSize: number
  showQuantity: boolean

  constructor(
    product: KitchenProductFormatted | KitchenComboFormatted,
    pickupType?: string,
    { fontSize = 24, showQuantity = true } = {}
  ) {
    this.product = product
    this.pickupType = pickupType
    this.quantityWidth = 50
    this.priceWidth = 50
    this.lineHeight = fontSize
    this.paddingBottom = 10
    this.fontSize = fontSize
    this.showQuantity = showQuantity
    if (!showQuantity) this.quantityWidth = 0
  }

  productName() {
    return this.product.name
  }

  height(ctx: CanvasRenderingContext2D, width: number) {
    ctx.font = this.font({ bold: true })
    let quantityWidth = this.quantityWidth
    if (!this.product.quantity.current) {
      quantityWidth *= 2
    }
    const infoWidth = width - quantityWidth
    ctx.font = this.font()
    const nameLines = splitLines(ctx, this.productName(), infoWidth).length
    const modifierLines = this.modifierLines()
      .map(modifier => {
        if (modifier.modifier.added) {
          ctx.font = this.font({ bold: true, small: true })
        } else {
          ctx.font = this.font({ small: true })
        }
        return splitLines(ctx, modifier.text, infoWidth).length
      })
      .reduce((total, lines) => (total += lines), 0)
    const comments = this.product?.comments?.text || ''
    ctx.font = this.font({ small: true })
    const commentsLines = splitLines(ctx, comments, infoWidth).length
    return (nameLines + modifierLines + commentsLines) * this.lineHeight + this.paddingBottom
  }

  modifierLines() {
    return this.product.modifiers.map(modifier => {
      let modifierLine = '+ ' + modifier.name
      if (modifier.quantity! > 1) {
        modifierLine += ' x ' + modifier.quantity
      }
      return { text: modifierLine, modifier }
    })
  }

  font({ bold = false, small = false } = {}) {
    let fontSize = this.fontSize
    if (small) fontSize *= 0.8
    let font = `${fontSize}px ${BODY_FONT}`
    if (bold) font = 'bold ' + font
    return font
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number) {
    const paddingTop = 20
    top += paddingTop
    ctx.font = this.font({ bold: true })
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    let quantityWidth = this.quantityWidth
    if (this.showQuantity) {
      if (this.product.quantity.current) {
        ctx.fillText(this.product.quantity.current + 'x', 0, top)
      } else {
        ctx.fillRect(0, top - 13, quantityWidth, 2)
        ctx.font = this.font()
        ctx.fillText(this.product.quantity.old + 'x', 0, top)
        ctx.font = this.font({ bold: true })
        ctx.fillText(this.product.quantity.new + 'x', quantityWidth, top)
        quantityWidth *= 2
        ctx.font = this.font()
      }
    }
    const infoWidth = width - quantityWidth
    ctx.font = this.font()
    top +=
      wrapText(ctx, this.productName(), quantityWidth, top, infoWidth, {
        lineHeight: this.lineHeight,
        strike: this.product.deleted
      }) * this.lineHeight
    this.modifierLines().forEach(modifierLine => {
      if (modifierLine.modifier.added) {
        ctx.font = this.font({ bold: true, small: true })
      } else {
        ctx.font = this.font({ small: true })
      }
      top +=
        wrapText(ctx, modifierLine.text, quantityWidth, top, infoWidth, {
          lineHeight: this.lineHeight,
          strike: modifierLine.modifier.deleted || this.product.deleted
        }) * this.lineHeight
    })
    ctx.font = this.font({ small: true })
    top +=
      wrapText(ctx, this.product.comments.text, quantityWidth, top, infoWidth, {
        lineHeight: this.lineHeight,
        strike: this.product.deleted
      }) * this.lineHeight
  }
}
